import type { StufenBeschreibung } from '@luzula/luzula-lib';

/**
 * Humusgehaltsstufen nach SEA 95, Teil B, Darst. B 3-14 („Gehalt an organischer
 * Substanz (Humusgehalt) in Masse-%", S. 71; wortgleich noch einmal in der
 * Analysenauswertung, S. 177).
 *
 * Bezugsbasis: Masse-% organische Substanz einer Bodenlage bei absolut
 * trockener Probe — NICHT der Humusvorrat eines Bodenblocks in dt/ha, der in
 * derselben Anweisung gesondert abgestuft wird (siehe [HUMUS_VORRAT_STUFEN]).
 *
 * Der Eingabewert in der Substratfolge IST die h-Zahl: `grube-profil-helpers`
 * schreibt `h${humusgehalt}` ins Profil, und `nwsk.ts` liest den Zuschlag unter
 * `h2` … `h6` aus der Bodenartentabelle. Deshalb steht hier der Wert und das
 * Kurzzeichen nebeneinander, statt eine zweite Zuordnung zu erfinden.
 *
 * Der frühere Hilfetext hatte die Reihe um eine Stufe verschoben (1 galt dort
 * als „humusfrei") und andere Spannen genannt. Die Werte hier sind am
 * Originallayout der Darstellung geprüft.
 */
export interface HumusStufe {
	/** Eingabewert in der Substratfolge (0 … 7). */
	wert: number;
	/** Kurzzeichen nach SEA 95; die Klasse über h6 führt keines („---"). */
	sea95?: string;
	/** Kurzzeichen nach SEA 74 — für Altformulare bei der Umkartierung. */
	sea74?: string;
	/** Überstrich über dem SEA-74-Zeichen (im Original ein bzw. zwei Striche). */
	strich?: 'einfach' | 'doppelt';
	/** Spanne in Masse-% organischer Substanz. */
	spanne: string;
	bezeichnung: string;
	/**
	 * Trägt einen Zuschlag auf den nutzbaren Wasserspeicher. Nur h2 … h6 stehen
	 * in der Bodenartentabelle; h0, h1 und die Klasse über h6 bringen nichts auf.
	 */
	mitZuschlag: boolean;
}

export const HUMUS_STUFEN: HumusStufe[] = [
	{ wert: 0, sea95: 'h0', spanne: '—', bezeichnung: 'humusfrei', mitZuschlag: false },
	{ wert: 1, sea95: 'h1', sea74: 'h″', spanne: '< 1 %', bezeichnung: 'sehr schwach humos', mitZuschlag: false },
	{ wert: 2, sea95: 'h2', sea74: 'h′', spanne: '1 … 2,5 %', bezeichnung: 'schwach humos', mitZuschlag: true },
	{ wert: 3, sea95: 'h3', sea74: 'h°', spanne: '2,5 … 5,0 %', bezeichnung: 'mäßig humos', mitZuschlag: true },
	{ wert: 4, sea95: 'h4', sea74: 'h', strich: 'einfach', spanne: '5 … 10 %', bezeichnung: 'stark humos', mitZuschlag: true },
	{ wert: 5, sea95: 'h5', sea74: 'h', strich: 'doppelt', spanne: '10 … 15 %', bezeichnung: 'sehr stark humos', mitZuschlag: true },
	{ wert: 6, sea95: 'h6', sea74: 'H', spanne: '15 … 30 %', bezeichnung: 'extrem humos', mitZuschlag: true },
	{ wert: 7, sea74: 'H', strich: 'einfach', spanne: '> 30 %', bezeichnung: 'Humusboden (mineralarm)', mitZuschlag: false },
];

/**
 * Farbtreppe von hell nach dunkel, Eingabewert = Index — nur zur Orientierung,
 * keine Aussage über die Bodenfarbe. Dieselbe Reihe färbt das Hilfefenster
 * (Legende) UND das Feld in der Substrattabelle; zwei Listen liefen auseinander.
 */
export const HUMUS_FARBEN: readonly string[] = [
	'#f6f1e7', '#e8dcc6', '#d4c09a', '#b99a68', '#95733f', '#6b4f27', '#472f14', '#2b1b0a',
];

/** Die Stufen für das Stufen-Feld (`wd-stufen-input`): Wert, Farbe, Name. */
export const HUMUS_STUFEN_FELD: StufenBeschreibung[] = HUMUS_STUFEN.map(s => ({
	wert: s.wert, farbe: HUMUS_FARBEN[s.wert], name: s.bezeichnung,
}));

/**
 * Stufen des Humusvorrates bis 80 cm Tiefe in dt/ha (SEA 95, Teil B, S. 145 und
 * S. 177).
 *
 * ⚠ Andere Bezugsbasis als [HUMUS_STUFEN]: Hier geht es um den Vorrat eines
 * ganzen Bodenblocks, dort um den Gehalt einer einzelnen Lage. Die beiden
 * Reihen lassen sich nicht ineinander umrechnen und dürfen im Fenster nicht
 * nebeneinander stehen, als wären es zwei Spalten derselben Tabelle.
 */
export const HUMUS_VORRAT_STUFEN: { bezeichnung: string; spanne: string }[] = [
	{ bezeichnung: 'sehr gering', spanne: '< 500' },
	{ bezeichnung: 'gering', spanne: '> 500 … 600' },
	{ bezeichnung: 'ziemlich gering', spanne: '> 600 … 800' },
	{ bezeichnung: 'mäßig', spanne: '> 800 … 1 200' },
	{ bezeichnung: 'ziemlich hoch', spanne: '> 1 200 … 2 000' },
	{ bezeichnung: 'hoch', spanne: '> 2 000 … 3 600' },
	{ bezeichnung: 'sehr hoch', spanne: '> 3 600 … 6 800' },
	{ bezeichnung: 'extrem hoch', spanne: '> 6 800' },
];
